import { useState, useEffect } from "react";
import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";

import { Select, SelectProps } from "@cofano/cck";
import { Box, IconButton } from "@mui/material";
import Add from "@mui/icons-material/Add";

import { DateUtils } from "utils/DateUtils";

import { CustomShip } from "view/charters/components/form/useCreateOrExistingCharterFormFields";
import CreateCharterDialog from "view/charters/components/dialog/CreateCharterDialog";

import { ChildPropsFromPagination } from "types/commons";
import { GetChartersForSelectDocument, GetChartersForSelectQuery } from "__generated__/view/charters/graphql/charter.queries.generated";
import { CharterSubtype, CharterType } from "__generated__/enums";

export type CharterSelectOption = ChildPropsFromPagination<GetChartersForSelectQuery, "charters">;

const CharterSelect = ({
	onCreate,
	withoutCreate,
	autoSelect = true,
	search,
	...rest
}: Omit<SelectProps<CharterSelectOption, false>, "fetchOptions" | "paginated"> & {
	onCreate?: (charter: CharterSelectOption) => void;
	withoutCreate?: boolean;
	autoSelect?: boolean;
	search: {
		charterType: CharterType;
		charterSubtypes: CharterSubtype[];
		start?: number | undefined;
		end?: number | undefined;
		creditors?: (number | undefined)[] | undefined;
		ship?: CustomShip | undefined;
		hasContract?: boolean;
		voyage?: { id: number; purchaseCharters?: CharterSelectOption[] } | undefined;
		validFrom?: number | undefined;
	};
}) => {

	const client = useApolloClient();
	const [ dialogOpen, openDialog ] = useState(false);

	const buildOptions = (defaultOptions: CharterSelectOption[]) => {

		const options = [ ...defaultOptions ];

		if (rest.value && !rest.value.id) {
			options.push({
				...rest.value,
				charterNumber: intl.formatMessage({ id: "NEW_CHARTER" }),
			});
		}

		if (rest.value && !options.some(o => o.id === rest.value?.id)) {
			options.push(rest.value);
		}

		return options;
	};

	const onFetchOptions = async() => {
		const voyageCharters = search.voyage?.purchaseCharters?.filter(c => c.id !== 0) || [];

		if (search.creditors || search.ship?.id) {
			const result = await client.query({
				query: GetChartersForSelectDocument,
				variables: {
					search: {
						charterType: search.charterType,
						charterSubtypes: search.charterSubtypes,
						creditors: search.creditors?.filter((c): c is number => !!c),
						checkForShipExplicitly: search.hasContract ? search.ship?.id : undefined,
						checkForShip: search.hasContract === undefined || search.hasContract === false ? search.ship?.id : undefined,
						hasContract: search.hasContract,
						validFrom: search.validFrom,
						timeWindowIncluded: search.start && search.end ? {
							from: DateUtils.getTimestamp(search.start),
							to: DateUtils.getTimestamp(search.end),
						} : undefined,
					},
				},
			});

			const searchResults = result.data.charters.content ?? [];
			const combinedCharters = [ ...voyageCharters ];

			searchResults.forEach(charter => {
				if (!combinedCharters.some(c => c.id === charter.id)) {
					combinedCharters.push(charter);
				}
			});

			return {
				options: buildOptions(combinedCharters),
				totalResults: combinedCharters.length,
			};
		} else if (search.voyage) {
			return {
				options: buildOptions(voyageCharters),
				totalResults: voyageCharters.length,
			};
		} else {
			return {
				options: buildOptions([]),
				totalResults: 0,
			};
		}
	};

	useEffect(() => {
		if (autoSelect && !rest.value && search.ship?.id && rest.onChange) {
			onFetchOptions().then(({ options }) => {
				const realCharters = options.filter(option => option.id);
				const selectableCharters = rest.getOptionDisabled
					? realCharters.filter(c => !rest.getOptionDisabled?.(c))
					: realCharters;

				if (selectableCharters.length === 1) {
					rest.onChange?.(selectableCharters[0], false);
				}
			});
		}
	}, [ autoSelect, rest.value, search.ship?.id ]);

	const currentValue = (rest.value && !rest.value.id) ? { ...rest.value, charterNumber: intl.formatMessage({ id: "NEW_CHARTER" }) } : rest.value;

	return (
		<Box sx={{ display: "flex" }}>
			<Box sx={{ flexGrow: 1 }}>
				<Select
					fetchOptions={onFetchOptions}
					clearable={false}
					getOptionLabel={option => option.charterNumber || intl.formatMessage({ id: "NEW_CHARTER" })}
					value={currentValue}
					groupBy={option => intl.formatMessage({ id: option.type })}
					loadingLabel={intl.formatMessage({ id: "LOADING_DATA" })}
					{...rest} />
			</Box>

			{!withoutCreate && (
				<Box sx={{ ml: 1 }}>
					<IconButton
						size="small"
						onClick={() => openDialog(true)}
						disabled={rest.disabled}>
						<Add />
					</IconButton>

					{search.ship &&
						<CreateCharterDialog
							open={dialogOpen}
							onClose={() => openDialog(false)}
							ship={search.ship}
							start={search.start}
							end={search.end}
							defaultCharterSubtype={search.ship?.defaultCharterSubtype ?? undefined}
							onCreate={c => {
								onCreate?.(c);
								openDialog(false);
							}} />
					}
				</Box>
			)}
		</Box>
	);
};

export default CharterSelect;
